import { useEffect, useState } from 'react';
import type { RuntimeMetric } from '@pm2-monitor/shared';
import { api } from '../../api/client';
import { useStore } from '../../state/store';
import { useWindowedSeries } from '../../state/useSeries';
import { axisLabels } from '../../lib/chart';
import { RANGE_LABELS, timeAxis } from '../../lib/derive';
import { formatMemory, toMiB } from '../../lib/format';
import { SeriesChart, XAxis, YAxis } from '../charts';
import { DefRow, Heading, Numeral, Panel, Placeholder, Sub } from '../ui';
import { MemoryBar } from './Overview';

export function Metrics() {
  const processes = useStore((s) => s.processes);
  const range = useStore((s) => s.range);
  const series = useWindowedSeries();

  const online = processes.filter((p) => p.status === 'online');
  const totalCpu = online.reduce((n, p) => n + p.cpu, 0);
  const totalMemory = online.reduce((n, p) => n + p.memory, 0);
  const peakMemory = Math.max(...online.map((p) => p.memory), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Heading>CPU · all processes</Heading>
            <Sub>Sum of per-process pm2 cpu, which is reported per core</Sub>
          </div>
          <Numeral value={totalCpu.toFixed(1)} unit="%" size={30} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <YAxis labels={axisLabels(series.cpuCeiling)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <SeriesChart
              height={190}
              gridlines={2}
              series={[
                {
                  values: series.plotCpu,
                  ceiling: series.cpuCeiling,
                  color: 'var(--pos)',
                  width: 2.5,
                  area: true,
                },
              ]}
            />
            <XAxis labels={timeAxis(series.coveredSeconds)} />
          </div>
        </div>
      </Panel>

      <Panel>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Heading>Memory · resident</Heading>
            <Sub>
              In-memory window only — the bridge keeps 60 samples, there is no long-term
              history.
              {series.partial
                ? ` Showing ${series.coveredSeconds}s of the ${RANGE_LABELS[range]} window, accumulated since this tab connected.`
                : ''}
            </Sub>
          </div>
          <Numeral value={toMiB(totalMemory).toFixed(0)} unit="MiB" size={30} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <YAxis labels={axisLabels(series.memCeiling)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <SeriesChart
              height={190}
              gridlines={2}
              series={[
                {
                  values: series.plotMem,
                  ceiling: series.memCeiling,
                  color: 'var(--muted)',
                  width: 2.5,
                },
              ]}
            />
            <XAxis labels={timeAxis(series.coveredSeconds)} />
          </div>
        </div>
      </Panel>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
          gap: 16,
        }}
      >
        <Panel style={{ padding: '22px 24px' }}>
          <Heading style={{ marginBottom: 18 }}>Memory by process</Heading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {online.length === 0 ? (
              <Placeholder title="Nothing online" />
            ) : (
              [...online]
                .sort((a, b) => b.memory - a.memory)
                .map((p) => (
                  <MemoryBar
                    key={p.id}
                    name={p.name}
                    value={formatMemory(p.memory)}
                    width={(p.memory / peakMemory) * 100}
                    fill={p.memory === peakMemory ? 'var(--pos)' : 'var(--ink)'}
                  />
                ))
            )}
          </div>
        </Panel>

        <RuntimePanel />
      </div>
    </div>
  );
}

/**
 * pm2's `axm_monitor` block — heap, event-loop latency, HTTP throughput. Only
 * processes running pm2's instrumentation publish it, so the empty state says
 * why rather than showing an empty list.
 */
function RuntimePanel() {
  const processes = useStore((s) => s.processes);
  const runtimeId = useStore((s) => s.runtimeId);
  const setRuntimeId = useStore((s) => s.setRuntimeId);
  const token = useStore((s) => s.token);

  const [metrics, setMetrics] = useState<RuntimeMetric[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || runtimeId === null) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { process } = await api.process(token, runtimeId);
        if (!cancelled) {
          setMetrics(process.metrics);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };

    void load();
    // These are polled rather than streamed: the status frame carries cpu and
    // memory only, and describing a process is a heavier call than the 1 Hz
    // tick should be making.
    const timer = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [token, runtimeId]);

  const selected = processes.find((p) => p.id === runtimeId);

  return (
    <Panel style={{ padding: '22px 24px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Heading>Runtime</Heading>
        <select
          value={runtimeId ?? ''}
          onChange={(e) => setRuntimeId(Number(e.target.value))}
          style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            borderRadius: 999,
            border: '1px solid var(--line)',
            background: 'var(--page)',
            color: 'var(--ink)',
            fontSize: 12.5,
            fontFamily: 'inherit',
          }}
        >
          {processes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <Sub style={{ marginBottom: 14 }}>
        From pm2's axm_monitor block — only processes running pm2's instrumentation
        publish it
      </Sub>

      {error ? (
        <Placeholder title="Could not read the process" detail={error} />
      ) : metrics === null ? (
        <Placeholder title="Loading…" />
      ) : metrics.length === 0 ? (
        <Placeholder
          title="No runtime metrics"
          detail={`${
            selected?.name ?? 'This process'
          } is not running pm2's instrumentation, so the daemon exposes cpu and memory only.`}
        />
      ) : (
        metrics.map((metric) => (
          <DefRow
            key={metric.name}
            label={metric.name}
            value={`${metric.displayValue}${metric.unit ? ` ${metric.unit}` : ''}`}
          />
        ))
      )}
    </Panel>
  );
}
